/* eslint-disable react/no-unescaped-entities */
import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import {
  Building2,
  Users,
  Calendar,
  Briefcase,
  Award,
  Globe,
  ArrowRight,
  CheckCircle,
} from "lucide-react";

const CorporatePage = () => {
  const fadeInUp = {
    initial: { opacity: 0, y: 30 },
    whileInView: { opacity: 1, y: 0 },
    viewport: { once: true },
    transition: { duration: 0.6 },
  };

  const services = [
    {
      icon: Briefcase,
      title: "Corporate Travel Management",
      desc: "Streamlined business travel solutions with preferential rates, policy compliance, and detailed reporting.",
    },
    {
      icon: Award,
      title: "Incentive Travel",
      desc: "Reward and motivate your top performers with unforgettable incentive trips designed to inspire.",
    },
    {
      icon: Calendar,
      title: "Conferences & Events",
      desc: "End-to-end planning and execution of conferences, seminars, and corporate events.",
    },
    {
      icon: Users,
      title: "Team Building",
      desc: "Engaging team-building retreats and activities that strengthen bonds and boost morale.",
    },
    {
      icon: Globe,
      title: "International Business Travel",
      desc: "Seamless arrangements for international business trips including visa support.",
    },
    {
      icon: Building2,
      title: "Venue Sourcing",
      desc: "Access to premium venues for meetings, conferences, and corporate gatherings.",
    },
  ];

  const stats = [
    { value: "100+", label: "Corporate Clients" },
    { value: "500+", label: "Events Managed" },
    { value: "50+", label: "Destinations" },
    { value: "98%", label: "Client Satisfaction" },
  ];

  return (
    <div data-testid="corporate-page">
      {/* Hero */}
      <section className="relative pt-32 pb-20 bg-slate-900" data-testid="corporate-hero">
        <div className="absolute inset-0">
          <img
            src="https://images.pexels.com/photos/3678057/pexels-photo-3678057.png?w=1920"
            alt="Corporate Travel"
            className="w-full h-full object-cover opacity-30"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/50 to-slate-900" />
        </div>
        <div className="container-custom relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="caption text-coral mb-4 block">Corporate & MICE</span>
            <h1 className="heading-1 text-white mb-6">Corporate Travel Solutions</h1>
            <p className="text-lg text-white/80">
              Elevate your business travel and events with our comprehensive corporate 
              and MICE services. From executive travel to large-scale conferences, we deliver excellence.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-16 bg-coral" data-testid="corporate-stats">
        <div className="container-custom">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-8">
            {stats.map((stat, index) => (
              <motion.div
                key={stat.label}
                initial={{ opacity: 0, scale: 0.8 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="text-center"
                data-testid={`corporate-stat-${index}`}
              >
                <div className="text-3xl md:text-4xl font-bold text-white mb-2">{stat.value}</div>
                <div className="text-white/80">{stat.label}</div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="section-padding bg-white" data-testid="corporate-services">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center mb-16">
            <span className="caption text-coral mb-4 block">Our Expertise</span>
            <h2 className="heading-2 text-primary mb-4">Corporate & MICE Services</h2>
            <p className="body-lg max-w-2xl mx-auto">
              Comprehensive solutions for all your corporate travel and event management needs
            </p>
          </motion.div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {services.map((service, index) => (
              <motion.div
                key={service.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <Card className="h-full hover:shadow-lg transition-shadow group" data-testid={`corp-service-${index}`}>
                  <CardContent className="p-8">
                    <div className="w-14 h-14 bg-coral/10 rounded-xl flex items-center justify-center mb-6 group-hover:bg-coral/20 transition-colors">
                      <service.icon className="h-7 w-7 text-coral" />
                    </div>
                    <h3 className="text-xl font-heading font-semibold text-primary mb-3">
                      {service.title}
                    </h3>
                    <p className="text-slate-600">{service.desc}</p>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="section-padding bg-accent" data-testid="corporate-why">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <motion.div {...fadeInUp}>
              <span className="caption text-coral mb-4 block">Why Dnotours</span>
              <h2 className="heading-2 text-primary mb-6">
                Your Trusted Corporate Travel Partner
              </h2>
              <p className="body-lg mb-8">
                With over 15 years of experience in corporate travel management, we understand 
                the unique needs of businesses. Our dedicated corporate team ensures seamless 
                execution of every trip and event.
              </p>
              <div className="space-y-4 mb-8">
                {[
                  "Single point of contact for all travel needs",
                  "Negotiated corporate rates with major airlines & hotels",
                  "Real-time travel tracking and expense management",
                  "24/7 emergency support for travelers",
                  "Customized travel policies and compliance",
                  "Detailed MIS reports and analytics",
                ].map((item, index) => (
                  <div key={index} className="flex items-center gap-3">
                    <CheckCircle className="h-5 w-5 text-coral flex-shrink-0" />
                    <span className="text-slate-600">{item}</span>
                  </div>
                ))}
              </div>
              <Link to="/corporate-proposal">
                <Button className="btn-coral" data-testid="corporate-cta-btn">
                  Request Corporate Proposal
                  <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </motion.div>
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
            >
              <img
                src="https://images.pexels.com/photos/4940642/pexels-photo-4940642.jpeg?w=800"
                alt="Corporate Meeting"
                className="rounded-2xl shadow-xl"
              />
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-coral" data-testid="corporate-final-cta">
        <div className="container-custom">
          <motion.div {...fadeInUp} className="text-center max-w-3xl mx-auto">
            <h2 className="heading-2 text-white mb-6">
              Ready to Transform Your Corporate Travel?
            </h2>
            <p className="text-lg text-white/90 mb-8">
              Let's discuss how we can streamline your business travel and deliver 
              exceptional MICE experiences.
            </p>
            <div className="flex flex-wrap justify-center gap-4">
              <Link to="/corporate-proposal">
                <Button className="bg-white text-coral hover:bg-white/90 h-12 px-8 rounded-full text-base font-medium" data-testid="corp-get-proposal-btn">
                  Get a Proposal
                </Button>
              </Link>
              <Link to="/contact">
                <Button variant="outline" className="border-white text-white hover:bg-white hover:text-coral h-12 px-8 rounded-full" data-testid="corp-contact-btn">
                  Contact Sales Team
                </Button>
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default CorporatePage;
